import { Settings } from "luxon";
import { Constructor } from "../types";
import { clearState, storeState } from "../util/taui-pref-storage";
import { TauiBaseEl } from "./taui-base-mixin";
import { identityGetIdentity } from "../data/identity/identity";
import { fireEvent } from "../common/dom/fire_event";
import { polyfillsLoaded } from "../common/translations/localize";
import { getMainError } from "../common/util/manager_error";
import { saveTokens } from "../common/auth/token_storage";

declare global {
  // for fire event
  interface TAUIDomEvents {
    "taui-refresh-current-user": undefined;
  }
}

export default <T extends Constructor<TauiBaseEl>>(superClass: T) =>
  class extends superClass {
    protected firstUpdated(changedProps) {
      super.firstUpdated(changedProps);
      this.addEventListener("taui-logout", () => this._handleLogout());
      this.addEventListener("taui-refresh-current-user", async () => {
        await this._getOwnIdentity();
      });
    }

    protected async tauiConnected() {
      super.tauiConnected();
      await this._getOwnIdentity();
    }

    private async _handleLogout() {
      try {
        await this.taui!.auth.revoke();
        // this.taui!.connection.close();
        clearState(false);
        document.location.href = "/";
      } catch (err: any) {
        // eslint-disable-next-line
        console.error(err);
        alert("Log out failed");
      }
    }

    private async _getOwnIdentity() {
      await identityGetIdentity(this.taui!, this.taui!.auth.idIdentity, {
        expandAndPermission: [
          { expand: "ssos", permission: "identity.identity.list" },
          // Expand roles to get resource group in all app
          { expand: "roles", permission: "identity.role.list" },
        ],
      }).then(
        async (data) => {
          const dataUser = { ...(data?.result || {}) };

          // @ts-ignore
          const locale = dataUser?.metadata?.config?.locale;

          if (
            locale?.language &&
            this.taui!.locale.language !== locale.language
          ) {
            fireEvent(this as any, "taui-language-select", locale.language);
          }

          if (
            locale?.number_format &&
            this.taui!.locale.number_format !== locale.number_format
          ) {
            fireEvent(
              this as any,
              "taui-number-format-select",
              locale.number_format
            );
          }

          if (
            locale?.time_format &&
            this.taui!.locale.time_format !== locale.time_format
          ) {
            fireEvent(
              this as any,
              "taui-time-format-select",
              locale.time_format
            );
          }

          let time_zone;
          if (
            locale?.time_zone &&
            this.taui!.locale.time_zone !== locale.time_zone
          ) {
            time_zone = locale.time_zone;
            fireEvent(this as any, "taui-time-zone-select", time_zone);
          } else {
            time_zone = "browser";
            if (this.taui!.locale.time_zone !== time_zone)
              fireEvent(this as any, "taui-time-zone-select", time_zone);
          }
          // Configure the default time zone
          Settings.defaultZone =
            time_zone === "browser"
              ? Intl.DateTimeFormat().resolvedOptions().timeZone
              : time_zone;

          if (__BUILD__ === "latest" && polyfillsLoaded) {
            polyfillsLoaded.then(() => {
              if ("__setDefaultTimeZone" in Intl.DateTimeFormat) {
                // @ts-ignore
                Intl.DateTimeFormat.__setDefaultTimeZone(time_zone);
              }
            });
          } else if ("__setDefaultTimeZone" in Intl.DateTimeFormat) {
            // @ts-ignore
            Intl.DateTimeFormat.__setDefaultTimeZone(time_zone);
          }

          this._updateTaui({ user: dataUser });
          storeState(this.taui!);
        },
        (err) => {
          if (err.error)
            fireEvent(this as any, "taui-notification", {
              message: getMainError(this.taui, err),
              type: "error",
            });

          if (
            err.status_code === 401 ||
            err.status_code === 403 ||
            err.status_code === 404
          ) {
            setTimeout(() => {
              saveTokens(null);
              location.reload();
            }, 2000);
          }
        }
      );
    }
  };
